import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { getClientCaseBySlug } from '../data/allClientCases';
import type { ClientCase } from '../data/allClientCases';
const EASE = [0.23, 1, 0.32, 1];

// Webapplicaties die we gebouwd hebben of nu bouwen.
const CASE_SLUGS = ['hulphondenforum', 'overnamehuys', 'mijn-bedrijfs-waarderingen'];

// Korte typering per case, zodat de kaart zegt wát voor app het is.
const appTypes: Record<string, string> = {
  hulphondenforum: 'Community-app',
  overnamehuys: 'Klantomgeving en taken',
  'mijn-bedrijfs-waarderingen': 'Samenwerkingstool'
};

// De Overnamehuys-case gaat over website én app. Op deze pagina tonen we de
// app-kant: eigen beeld, eigen omschrijving en het label "In ontwikkeling".
const cardOverrides: Record<string, {
  image?: string;
  description?: string;
  inDevelopment?: boolean;
}> = {
  overnamehuys: {
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/3d0f736b-5132-4a5b-afa9-7d19fb9c931a.jpg',
    description: 'Na de website bouwen we voor Overnamehuys een app met een klantomgeving om veilig documenten te delen en taken voor medewerkers, voor een soepelere samenwerking.',
    inDevelopment: true
  }
};
export const WebappCases: React.FC = () => {
  const cases = CASE_SLUGS.map(getClientCaseBySlug).filter((c): c is ClientCase => Boolean(c));
  if (cases.length === 0) return null;
  return <section className="w-full bg-white py-20 md:py-24" data-id="element-893" data-mp="2g3kmhls1szj1">
      <div className="container mx-auto max-w-[1200px] px-4 md:px-6 lg:px-8" data-id="element-894" data-mp="2k5pbu8bgr9zv">
        <motion.div className="mb-12 max-w-2xl" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-895" data-mp="2k3kxsxjuoulv">
          <h2 className="mb-4 text-3xl font-bold text-[#111827] md:text-4xl" data-id="element-896" data-mp="2ysqx0liouktr">
            Apps die we bouwden
          </h2>
          <p className="text-lg leading-relaxed text-[#6B7280]" data-id="element-897" data-mp="2e5j90r7g9au5">
            Van een besloten community tot een klantomgeving voor een
            adviesbureau. Elke app is anders, omdat elke organisatie anders
            werkt.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-898" data-mp="2nwx3gkkjnkgf">
          {cases.map((clientCase, index) => {
          const override = cardOverrides[clientCase.slug] ?? {};
          return <motion.a key={clientCase.slug} href={`/cases/${clientCase.slug}`} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.3,
            delay: index * 0.05,
            ease: EASE
          }} className="group flex flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white transition-[border-color,box-shadow] duration-200 hover:border-[#004AC7]/40 hover:shadow-lg" data-id="element-899" data-mp="2wuftk3d1ooz6" data-mpi={index}>
              <div className="relative h-52 overflow-hidden bg-[#EEF5FA]" data-id="element-900" data-mp="3j57o08t9p8eg">
                <img src={override.image ?? clientCase.heroImage} alt={`${clientCase.clientName}, webapplicatie gebouwd door Noord Digital`} loading="lazy" className={`h-full w-full transition-transform duration-300 group-hover:scale-[1.03] ${!override.image && clientCase.heroImageContain ? 'object-contain p-6 pt-12' : 'object-cover'}`} data-id="element-901" data-mp="3o2r298pytl6e" />
                <div className="absolute left-4 top-4 flex flex-wrap gap-2" data-id="element-902" data-mp="30gau461s8hwr">
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#004AC7] shadow-sm" data-id="element-903" data-mp="34hcwuwuwrc3h">
                    {appTypes[clientCase.slug] ?? 'Webapplicatie'}
                  </span>
                  {(override.inDevelopment || clientCase.status === 'in-ontwikkeling') && <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800 shadow-sm" data-id="element-904" data-mp="opp0b2jt3ll7">
                      In ontwikkeling
                    </span>}
                </div>
              </div>
              <div className="flex flex-1 flex-col p-7" data-id="element-905" data-mp="2pq3oyk0jrhtm">
                <h3 className="mb-3 text-xl font-bold text-[#111827] transition-colors duration-200 group-hover:text-[#004AC7]" data-id="element-906" data-mp="2kra76fe894nn">
                  {clientCase.clientName}
                </h3>
                <p className="mb-6 flex-1 leading-relaxed text-[#6B7280]" data-id="element-907" data-mp="2brn65upeu0v4">
                  {override.description ?? clientCase.shortDescription}
                </p>
                <span className="mt-auto inline-flex items-center gap-1.5 font-semibold text-[#004AC7]" data-id="element-908" data-mp="26tvg768bsotk">
                  Lees het verhaal
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-909" data-mp="1x1fz0b4us49q" />
                </span>
              </div>
            </motion.a>;
        })}
        </div>
      </div>
    </section>;
};